<template>
  <div class="reorganize-tree-node" :class="{ 'reorganize-tree-node--just-created': isJustCreated }">
    <div class="reorganize-tree-node__name">
      <Bars3Icon v-if="!isItRoot" aria-hidden="true" class="reorganize-tree-node__handle handle" />
      <span ref="nameRef" v-tooltip.permanent="nameTooltip" class="reorganize-tree-node__name-value">{{ displayedName }}</span>
      <div class="reorganize-tree-node__actions">
        <span v-if="isAmountVisible" class="reorganize-tree-node__amount">{{ amount }}</span>
        <template v-if="!isItRoot">
          <UIButton
            v-tooltip.top="'Delete'"
            variant="danger"
            fill="light"
            size="xs"
            :leading="TrashIcon"
            @click="handleClickDelete"
          />
        </template>
        <UIButton
          v-tooltip.top="'Add child'"
          fill="light"
          size="xs"
          :leading="PlusIcon"
          @click="handleClickAddChild"
        />
      </div>
    </div>
    <div class="reorganize-tree-node__container" :class="containerClasses">
      <div class="reorganize-tree-node__branch">
        <div
          v-if="!isItRoot && children.length > 0"
          v-tooltip.top="isChildrenHidden ? 'Show children' : 'Hide children'"
          class="reorganize-tree-node__branch-circle"
          :class="{ 'reorganize-tree-node__branch-circle--open': !isChildrenHidden }"
          @click="handleToggleChildren"
        >
          <ChevronRightIcon aria-hidden="true" />
        </div>
      </div>
      <draggable
        group="nodes"
        :model-value="children"
        handle=".handle"
        tag="div"
        :animation="200"
        :emptyInsertThreshold="5"
        :itemKey="(element: string) => element"
        class="reorganize-tree-node__area"
        @choose="handleChoose"
        @unchoose="handleUnChoose"
        @change="handleChange"
      >
        <template v-if="children.length === 0" #header><span class="reorganize-tree-node__area-empty">No children</span></template>
        <template v-else-if="isChildrenHidden" #header><div class="reorganize-tree-node__area-toggle" @click="handleToggleChildren">{{ children.length }} hidden child{{ children.length > 1 ? 'ren' : '' }}</div></template>
        <template #item="{ element }">
          <ReorganizeTreeNode
            v-if="!isChildrenHidden"
            :tree
            :path="[...path, element]"
            :amounts-map
            :entry-settings
            @click:create="handleCreate"
            @click:add="path => emit('click:add', path)"
            @click:remove="path => emit('click:remove', path)"
            @click:delete="path => emit('click:delete', path)"
            @choose="path => emit('choose', path)"
            @unchoose="emit('unchoose')"
          />
        </template>
      </draggable>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, ref, useTemplateRef } from 'vue'
import { useResizeObserver } from '@vueuse/core'
import { get } from 'lodash'
import draggable from 'vuedraggable'

import { TransactionSettings } from '@types'
import { ReorganizeTreeItem } from './utils/types'

import { displayAmount } from '@/entities/transactions/utils/helpers'

import { UIButton } from '@ui/buttons'
import { Bars3Icon, ChevronRightIcon, PlusIcon, TrashIcon } from '@heroicons/vue/24/outline'

type Props = {
  tree: ReorganizeTreeItem
  path: string[]
  entrySettings?: TransactionSettings['entries'][0]
  amountsMap?: Map<string, number>
}

type Emits = {
  'click:create': [path: string[], callback: () => void]
  'click:remove': [path: string[]]
  'click:add': [path: string[]]
  'click:delete': [path: string[]]
  'choose': [path: string[]]
  'unchoose': []
}

const emit = defineEmits<Emits>()
const props = defineProps<Props>()

const getAssetName = inject('getAssetName', (id: string) => id)

const nameRef = useTemplateRef<HTMLSpanElement>('nameRef')

const isNameEllipsis = ref(false)

const isChildrenHidden = ref(props.path.length !== 1)

const justCreatedPath = inject('justCreatedPath', ref<string>(''))

const isJustCreated = computed(() => justCreatedPath.value === JSON.stringify(props.path))

const isItRoot = computed(() => props.path.length === 1)

const isAmountVisible = computed(() => props.path.length > 2)

const currentNode = computed(() => get(props.tree, props.path) as ReorganizeTreeItem)

const children = computed(() => Object.keys(currentNode.value))

const displayedName = computed(() => getAssetName(props.path.at(-1) || ''))

const nameTooltip = computed(() => {
  if (!isNameEllipsis.value) return
  return {
    content: displayedName.value,
    handleResize: true,
  }
})

const containerClasses = computed(() => ({
  'reorganize-tree-node__container--empty': children.value.length === 0,
}))

const amount = computed(() => amountFormatter(props.amountsMap?.get(props.path.join('-')) || 0))

const amountFormatter = (value: number | null | undefined) => {
  const settings = props.entrySettings?.amount
  if (!settings) return ''
  const isPercent = settings?.rule === 'percent'
  return displayAmount(value, isPercent)
}

const handleCreate = (path: string[], callback: () => void) => {
  emit('click:create', path, callback)
}

const handleClickAddChild = () => {
  emit('click:create', props.path, () => {
    isChildrenHidden.value = false
  })
}

const handleClickDelete = () => {
  emit('click:delete', props.path)
}

const handleChange = (event: any) => {
  if (event.added) {
    emit('click:add', [...props.path, event.added.element])
  } else if (event.removed) {
    emit('click:remove', [...props.path, event.removed.element])
  }
}

const handleChoose = (event: any) => {
  const element = children.value[event.oldIndex]
  emit('choose', [...props.path, element])
}

const handleUnChoose = () => {
  emit('unchoose')
}

const handleToggleChildren = () => {
  isChildrenHidden.value = !isChildrenHidden.value
}

useResizeObserver(nameRef, () => {
  if (!nameRef.value) return
  isNameEllipsis.value = nameRef.value.offsetWidth < nameRef.value.scrollWidth
})
</script>

<style scoped>
.reorganize-tree-node {
  @apply flex flex-col;

  &__handle {
    @apply w-4 h-4 cursor-move;
  }

  &__amount {
    @apply text-[10px] text-light-text font-normal;
    @apply bg-body-dark;
    @apply px-1 mr-0.5;
    @apply rounded-sm;
  }

  &__name {
    @apply w-full h-[2.25rem];
    @apply flex items-center gap-1;
    @apply pl-2 pr-0.5;
    @apply text-sm font-medium;
    @apply border border-common-border;
    @apply hover:border-indigo-300 dark:hover:border-gray-500;
    @apply bg-body;
    @apply rounded;
  }

  &__name-value {
    @apply flex-auto;
    @apply truncate;
  }

  &__actions {
    @apply flex items-center;

    .ui-button {
      @apply p-2;
    }
  }

  &__container {
    @apply flex;
  }

  &__branch {
    @apply w-3 h-5;
    @apply relative shrink-0;
    @apply ml-2;
    @apply border-l border-b border-common-border;
    @apply rounded-bl-sm;
  }

  &__branch-circle {
    @apply size-4;
    @apply absolute bottom-0 -left-0 -translate-x-1/2 translate-y-1/2;
    @apply flex justify-center items-center;
    @apply bg-gray-100 hover:bg-gray-200;
    @apply dark:bg-gray-800 dark:hover:bg-gray-900;
    @apply text-indigo-700 dark:text-indigo-300;
    @apply rounded-full;
    @apply cursor-pointer;

    &--open {
      @apply bg-indigo-700 hover:bg-indigo-600;
      @apply dark:bg-indigo-300 dark:hover:bg-indigo-400;
      @apply text-white dark:text-gray-900;
      @apply rotate-90;
    }

    svg {
      @apply size-3;
    }
  }

  &__area {
    @apply min-h-8;
    @apply relative flex-auto;
    @apply flex flex-col gap-1;
    @apply p-2 mt-1;
    @apply border border-dashed border-common-border;
    @apply rounded;
    @apply overflow-hidden;

    .reorganize-tree:has(.sortable-chosen) & {
      @apply border-indigo-300 dark:border-gray-500;
    }
  }

  &__area-empty,
  &__area-toggle {
    @apply absolute inset-0;
    @apply flex items-center justify-center;
    @apply text-xs text-light-text;
  }

  &__area-toggle:hover {
    @apply text-indigo-700 dark:text-indigo-300;
    @apply cursor-pointer;
  }

  &:has(> .reorganize-tree-node__name:hover) > &__container > &__branch,
  &:has(> .reorganize-tree-node__name:hover) > &__container > &__area {
    @apply border-indigo-300 dark:border-gray-500;
  }
}
</style>
